/**
 * Dashboard empty states (APP-002 §17).
 *
 * Real empty states for API keys, bulk jobs, and webhooks. Each uses only
 * backend-provided signals (counts, plan/action info) and never fabricates
 * delivery health or promises plan features the account may not support.
 * Links go to the real feature routes (APP-002 §9, §17).
 */
import { Link } from 'react-router-dom';
import { EmptyState } from '@/components/feedback/EmptyState';
import { Button } from '@/components/ui/button';
import { Key, FileStack, Webhook } from '@/components/icons';

/** No API keys: a clear message + CTA to the (placeholder) API Keys page. */
export function NoApiKeysEmpty() {
  return (
    <EmptyState
      icon={<Key className="h-6 w-6" />}
      title="No API keys yet"
      description="Create an API key to start analyzing signups from your application."
      action={
        <Button size="sm" asChild>
          <Link to="/api-keys">Go to API Keys</Link>
        </Button>
      }
    />
  );
}

/**
 * No bulk jobs. Bulk scoring is plan-gated (Growth+); we do not promise
 * availability if the plan does not support it. The dashboard overview does
 * not return a recent-jobs list, so this is a purposeful empty section rather
 * than an extra call (APP-002 §18). APP-007 made /bulk a real route.
 */
export function NoBulkJobsEmpty({ bulkSupported }: { bulkSupported: boolean }) {
  return (
    <EmptyState
      icon={<FileStack className="h-6 w-6" />}
      title="No bulk scoring jobs"
      description={
        bulkSupported
          ? 'Bulk scoring lets you process many signups at once. Upload a CSV to start a job.'
          : 'Bulk scoring is available on eligible plans (Growth and above).'
      }
      action={
        <Button size="sm" variant="outline" asChild>
          <Link to="/bulk">Go to Bulk Scoring</Link>
        </Button>
      }
    />
  );
}

/**
 * No webhooks. Customer webhooks are plan-gated (Growth+); we do not fabricate
 * delivery health. Link goes to the placeholder page.
 */
export function NoWebhooksEmpty({ webhooksSupported }: { webhooksSupported: boolean }) {
  return (
    <EmptyState
      icon={<Webhook className="h-6 w-6" />}
      title="No webhooks configured"
      description={
        webhooksSupported
          ? 'Customer webhooks notify your service when supported events occur.'
          : 'Customer webhooks are available on eligible plans (Growth and above).'
      }
      action={
        <Button size="sm" variant="outline" asChild>
          <Link to="/webhooks">Go to Webhooks</Link>
        </Button>
      }
    />
  );
}
